import type { FC } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { CONTRACT_BUILDER_HEADER_Z_INDEX } from '@constants/z-indices';
import { responsiveTruncateClasses } from '@ContractBuilder/contract-builder-header/classes';
import type { EntityData } from '@domain/types/entity';
import { DocumentDuplicateIcon, HashtagIcon, TemplateIcon } from '@heroicons/react/solid';
import { isPPLFrontend } from '@root/src/utils/app-paths';
import { useDocumentLocation } from '@src/hooks';
import clsx from 'clsx';

import { EditableBrandingField } from './EditableBrandingField';
import { EditableClassOfBusinessField } from './EditableClassOfBusinessField';
import { EditableTeamsField } from './EditableTeamsField';
import { EditableTemplateBaseField } from './EditableTemplateBaseField';
interface EntityDetailsProps {
  className?: string;
  entity: EntityData;
  hasOperationsDisabled: boolean;
  onSave: (name: string, value?: string | number[]) => Promise<void>;
  onUpdateBaseTemplate: (sourceTemplateId: string, cb: () => void) => Promise<void>;
}

const getSourceType = (resourceName?: string): string => {
  if (!resourceName) return '';
  return resourceName.startsWith('submission') ? 'Duplicated contract' : 'Base template';
};

const getSourceTypeIconComponent = (resourceName?: string) => {
  if (!resourceName) return null;
  return resourceName.startsWith('submission') ? DocumentDuplicateIcon : TemplateIcon;
};

export const EntityDetails: FC<EntityDetailsProps> = ({
  className,
  entity,
  hasOperationsDisabled,
  onSave,
  onUpdateBaseTemplate,
}) => {
  const { isTemplate, isSubmission } = useDocumentLocation();

  const sourceType = getSourceType(entity.sourceResourceName);
  const sourceName = entity.sourceResourceDisplayName;
  const SourceTypeIconComponent = getSourceTypeIconComponent(entity.sourceResourceName);

  const pplOperationsDisabled = (isPPLFrontend && !isTemplate) || hasOperationsDisabled;

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-1 text-sm text-gray-600',
        CONTRACT_BUILDER_HEADER_Z_INDEX,
        className,
      )}
    >
      <Show when={!!entity.ref && (isPPLFrontend ? isTemplate : true)}>
        <Tooltip content={`${isTemplate ? 'Template' : 'Contract'} ID`}>
          <span>
            <HashtagIcon className="h-4 w-4" />
          </span>
        </Tooltip>
        <p className={responsiveTruncateClasses('mr-3 cursor-default')}>{entity.ref}</p>
      </Show>
      <Show when={!isTemplate}>
        <Tooltip content={sourceType}>
          <span>{SourceTypeIconComponent && <SourceTypeIconComponent className="h-[1.15rem] w-[1.15rem]" />}</span>
        </Tooltip>
        <ShowFirstMatching>
          <Show when={isSubmission}>
            <EditableTemplateBaseField
              className="mr-3"
              entity={entity}
              hasOperationsDisabled={hasOperationsDisabled}
              onSave={onUpdateBaseTemplate}
            />
          </Show>
          <Show when={true}>
            <p className={responsiveTruncateClasses('mr-3 cursor-default')}>{sourceName}</p>
          </Show>
        </ShowFirstMatching>
      </Show>

      <EditableBrandingField entity={entity} hasOperationsDisabled={hasOperationsDisabled} onSave={onSave} />
      <Show when={isPPLFrontend ? isTemplate : true}>
        <EditableClassOfBusinessField entity={entity} hasOperationsDisabled={hasOperationsDisabled} onSave={onSave} />
      </Show>
      <EditableTeamsField entity={entity} hasOperationsDisabled={pplOperationsDisabled} onSave={onSave} />
    </div>
  );
};
